import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

// Add plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);

/**
 * Core utility for consistent date handling across the application
 * Ensures UI display matches the actual date value
 */
export const dateUtils = {
  /**
   * Formats a date to YYYY-MM-DD ensuring consistency between UI and value
   * @param {Date|string|null} date - The date to format
   * @returns {string} Formatted date string or empty string if invalid
   */
  formatToYYYYMMDD: (date) => {
    if (!date) return "";
    return dayjs(date).format("YYYY-MM-DD");
  },

  /**
   * Creates a Date object for the date picker that matches the UI display
   * @param {string|Date|null} date - The date to convert
   * @returns {Date|null} Date object or null if invalid
   */
  createDateForPicker: (date) => {
    if (!date) return null;
    // Create date object and ensure it's in local timezone at start of day
    return dayjs(date).startOf("day").toDate();
  },

  /**
   * Handles date picker onChange events consistently
   * @param {Date|null} newDate - The new date from the picker
   * @param {Function} callback - Callback to handle the formatted date
   */
  handleDateChange: (newDate, callback) => {
    if (!newDate) {
      callback("");
      return;
    }

    // Format the date consistently
    const formattedDate = dayjs(newDate).format("YYYY-MM-DD");

    console.groupEnd();

    callback(formattedDate);
  },

  /**
   * Validates if a date string is in YYYY-MM-DD format
   * @param {string} dateString - The date string to validate
   * @returns {boolean} True if valid format
   */

  /**
   * Formats a date to YYYY-MMM-DD (e.g., 2024-DEC-31)
   * If the date is invalid or missing, it returns "--"
   * @param {Date|string|null} date - The date to format
   * @returns {string} Formatted date string or "--" if invalid
   */
  formatToYYYYMonDD: (date) => {
    if (!date) return "--";
    return dayjs(date).format("YYYY-MMM-DD").toUpperCase();
  },

  isValidDateFormat: (dateString) => {
    if (!dateString) return false;
    return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  },

  /**
   * Compares two dates
   * @param {string|Date} date1 - First date
   * @param {string|Date} date2 - Second date
   * @returns {number} -1 if date1 < date2, 0 if equal, 1 if date1 > date2
   */
  compareDates: (date1, date2) => {
    if (!date1 || !date2) return 0;
    const d1 = dayjs(date1);
    const d2 = dayjs(date2);
    if (d1.isBefore(d2)) return -1;
    if (d1.isAfter(d2)) return 1;
    return 0;
  },

  /**
   * Adds specified number of days to a date
   * @param {string|Date} date - The starting date
   * @param {number} days - Number of days to add
   * @returns {string} Resulting date in YYYY-MM-DD format
   */
  addDays: (date, days) => {
    if (!date) return "";
    return dayjs(date).add(days, "day").format("YYYY-MM-DD");
  },

  /**
   * Subtracts specified number of days from a date
   * @param {string|Date} date - The starting date
   * @param {number} days - Number of days to subtract
   * @returns {string} Resulting date in YYYY-MM-DD format
   */
  subtractDays: (date, days) => {
    if (!date) return "";
    return dayjs(date).subtract(days, "day").format("YYYY-MM-DD");
  },

  /**
   * Gets today's date in YYYY-MM-DD format
   * @returns {string} Today's date in YYYY-MM-DD format
   */
  today: () => {
    return dayjs().format("YYYY-MM-DD");
  },

  /**
   * Calculates working hours between two dates based on percentage allocation
   * @param {string|Date} startDate - Start date in YYYY-MM-DD format
   * @param {string|Date} endDate - End date in YYYY-MM-DD format
   * @param {number} percentage - Percentage allocation (0-100)
   * @returns {number} Total working hours
   */

  calculateComplementaryPercentage: (percentage, originalPercentage) => {
    // For 75% splits: if 25 is selected, other must be 50 and vice versa
    if (originalPercentage === 75) {
      return percentage === 25 ? 50 : 25;
    }
    // For 50% splits: both must be 25
    else if (originalPercentage === 50) {
      return 25;
    }
    // For 100% splits: normal complementary calculation
    else {
      return 100 - percentage;
    }
  },

  /**
   * Formats a datetime to YYYY MMM DD HH:mm:ss format preserving UTC time
   * @param {Date|string|null} datetime - The datetime to format
   * @returns {string} Formatted datetime string or "--" if invalid
   */
  formatToYYYYMonDDHHMMSS: (datetime) => {
    if (!datetime) return "--";

    // Parse the input datetime and keep it in UTC
    const utcDate = dayjs.utc(datetime);

    // Format in UTC without converting to local timezone
    return utcDate.format("YYYY MMM DD HH:mm:ss").toUpperCase();
  }
};

// Export individual functions for convenience
export const {
  formatToYYYYMMDD,
  formatToYYYYMonDD,
  formatToYYYYMonDDHHMMSS,
  createDateForPicker,
  handleDateChange,
  isValidDateFormat,
  compareDates,
  addDays,
  subtractDays,
  today,

  calculateComplementaryPercentage
} = dateUtils;
